import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { useYupValidationResolver } from "@ryde/redux";
import {
  errors,
  IDropdownItem,
  Log,
  RecaptchaEnterpriseActions,
} from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  createActiveStepError,
  createMessage,
  ITnufaLoginLandingModel,
  landingDropdownStationListAtom,
  landingMessagesAtom,
  LandingStepEnum,
  LandingStepErrorsEnum,
  LoginStepFormFieldsEnum,
  TnufaLoginStepFormFieldsEnum,
  useLandingActions,
  useLandingApiAuth,
  useLandingApiUnits,
  useLandingOtpErrors,
  useLandingRecaptchaToken,
} from "@ryde/utils-landings";

import { useYupLoginLandingTnufaValidationSchema } from "./parts";

export const useStepLoginTnufa = () => {
  const { goToStep, setActiveStepErrors } = useLandingActions();
  const { requestPublicOtp } = useLandingApiAuth();
  const { handleErrors } = useLandingOtpErrors();
  const { getRecaptchaToken } = useLandingRecaptchaToken();
  const setServerErrors = useSetRecoilState(landingMessagesAtom);
  const setApiLoading = useSetRecoilState(apiDataFetchingLandingAtom);
  const setStationsDropdownList = useSetRecoilState(
    landingDropdownStationListAtom,
  );
  const [unitsDropdownList, setUnitsDropdownList] = useState<IDropdownItem[]>(
    [],
  );
  const { getUnitsDropdownItems, getStationsDropdownItems } =
    useLandingApiUnits();
  const { yupLoginLandingTnufaValidationSchema } =
    useYupLoginLandingTnufaValidationSchema();
  const resolver = useYupValidationResolver(
    yupLoginLandingTnufaValidationSchema,
    "landing",
  );

  const methods = useForm<ITnufaLoginLandingModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      firstName: "",
      lastName: "",
      unitId: undefined,
      phone: "",
      sourceStation2Id: undefined,
      acceptTerms: false,
      acceptPrivacyPolicy: false,
    },
    resolver,
  });

  const {
    getValues,
    clearErrors,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const firstNameValue = watch(TnufaLoginStepFormFieldsEnum.FirstName);
  const lastNameValue = watch(TnufaLoginStepFormFieldsEnum.lastName);
  const unitIdValue = watch(TnufaLoginStepFormFieldsEnum.Unit);
  const phoneWatchValue = watch(LoginStepFormFieldsEnum.Phone);
  const sourceStation2IdValue = watch(
    TnufaLoginStepFormFieldsEnum.SourceStation2Id,
  );
  const acceptTermsValue = watch(TnufaLoginStepFormFieldsEnum.AcceptTerms);
  const acceptPrivacyPolicyValue = watch(
    TnufaLoginStepFormFieldsEnum.AcceptPrivacyPolicy,
  );

  useEffect(() => {
    if (acceptTermsValue !== undefined) {
      trigger(TnufaLoginStepFormFieldsEnum.AcceptTerms);
    }
  }, [acceptTermsValue, trigger]);

  useEffect(() => {
    if (acceptPrivacyPolicyValue !== undefined) {
      trigger(TnufaLoginStepFormFieldsEnum.AcceptPrivacyPolicy);
    }
  }, [acceptPrivacyPolicyValue, trigger]);

  const isContinueCanBeApply = useMemo(() => {
    return (
      isValid &&
      firstNameValue &&
      lastNameValue &&
      unitIdValue &&
      sourceStation2IdValue &&
      phoneWatchValue &&
      acceptTermsValue &&
      acceptPrivacyPolicyValue
    );
  }, [
    isValid,
    firstNameValue,
    lastNameValue,
    unitIdValue,
    sourceStation2IdValue,
    phoneWatchValue,
    acceptTermsValue,
    acceptPrivacyPolicyValue,
  ]);

  const handleLogin = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    setApiLoading(true);
    setServerErrors(createMessage([]));
    const firstName = getValues(TnufaLoginStepFormFieldsEnum.FirstName);
    const lastName = getValues(TnufaLoginStepFormFieldsEnum.lastName);
    const unitId = getValues(TnufaLoginStepFormFieldsEnum.Unit);
    const phone = getValues(TnufaLoginStepFormFieldsEnum.Phone);
    const stationId = getValues(TnufaLoginStepFormFieldsEnum.SourceStation2Id);
    const recaptchaToken = await getRecaptchaToken(
      RecaptchaEnterpriseActions.REQUEST_OTP,
    );

    try {
      const requestOtpResponse = await requestPublicOtp({
        allowExistingPassenger: false,
        recaptchaToken,
        phone,
      });

      const { success, error } = requestOtpResponse ?? {};

      if (success) {
        setServerErrors(createMessage([]));
        goToStep(LandingStepEnum.VerificationStep, {
          firstName,
          lastName,
          phone,
          unitId,
          sourceStation2Id: stationId,
        });
      } else {
        handleErrors({
          error,
          defaultAction(e) {
            setServerErrors(createMessage([errors.auth.errorWhileRequestOTP]));
          },
        });
      }
    } catch (error) {
      Log.warn("Error while requesting OTP", error);
      setActiveStepErrors(
        createActiveStepError(LandingStepErrorsEnum.GeneralError),
      );
      goToStep(LandingStepEnum.ErrorStep);
    } finally {
      setApiLoading(false);
    }
  });

  useEffect(() => {
    const loadUnitsDropdown = async (): Promise<void> => {
      try {
        const response = await getUnitsDropdownItems();
        const { success, data } = response ?? {};

        if (success && data) {
          setUnitsDropdownList(data);
        }
      } catch (error) {
        Log.error("Failed to load units dropdown", error);
      }
    };

    loadUnitsDropdown();
  }, [getUnitsDropdownItems]);

  useEffect(() => {
    const loadStationsDropdown = async (): Promise<void> => {
      try {
        const response = await getStationsDropdownItems({
          isOfficial: true,
          isDestination: false,
        });
        const { success, data } = response ?? {};

        if (success && data) {
          setStationsDropdownList(data);
        }
      } catch (error) {
        Log.warn("Failed to load units dropdown", error);
      }
    };

    loadStationsDropdown();
  }, [getStationsDropdownItems, setStationsDropdownList]);

  return { methods, unitsDropdownList, handleLogin, isContinueCanBeApply };
};
